/*
    Converts truthy/falsy strings from the API to booleans.
    The API returns a string.
*/
export const toBool = (val) => {
  return val === '1' || val === 'true' || val === true;
};

/**
 * Formats a phone number string into a US phone number format: (###) ###-####
 *
 * @param {string | number | null | undefined} number - The phone number string to format
 * @returns {string} The formatted phone number string
 */
export function phoneNumber(unformatted) {
  if (!unformatted) return '';
  const cleaned = unformatted.toString().replace(/\D/g, '');

  return cleaned.length === 7
    ? cleaned.replace(/(\d{3})(\d{4})/, '$1-$2')
    : cleaned.length === 10
      ? cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
      : '';
}

export function concatFirstAndLastNames(first, last) {
  const concatenated = `${first ?? ''} ${last ?? ''}`;
  return concatenated.trim();
}

export function parseDate(date) {
  const dateObject = new Date(date);
  return dateObject.toString() === 'Invalid Date'
    ? new Date(date && date.replace && date.replace(' ', 'T'))
    : dateObject;
}

export function datetime(date, truncateTime) {
  if (!date) return;
  const truncateOptions = {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  return parseDate(date).toLocaleString(undefined, truncateTime ? truncateOptions : undefined);
}

export function date(date) {
  return parseDate(date).toLocaleDateString();
}

export function time(date, truncateTime) {
  const truncateOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  return parseDate(date).toLocaleTimeString(undefined, truncateTime ? truncateOptions : undefined);
}

export function formatShortDate(date) {
  return parseDate(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatShortTime(date) {
  return parseDate(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function money(
  amount,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  showCurrencySymbol = true,
) {
  return typeof amount === 'boolean' ||
    typeof amount === 'undefined' ||
    typeof amount === 'object' ||
    Number(amount).toString() === 'NaN' ||
    Math.abs(amount) === Infinity ||
    amount === ''
    ? ''
    : Number(amount).toLocaleString('en-US', {
        currency: 'USD',
        minimumFractionDigits, // passing in 0 for min and max removes decimal
        maximumFractionDigits,
        style: showCurrencySymbol ? 'currency' : 'decimal',
      });
}

export function percent({
  amount,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  showPercentSymbol = true,
  /**
   * If amount is normalized, this function will assume 'amount' is in the [0,1] range
   * where 0 is 0% and 1 is 100%.
   *
   * Examples: (assumes minimumFractionDigits = 2)
   *   amountNormalized = true
   *   input: 0.25
   *   output: 25%
   *   input: 25
   *   output: 2,500%
   *
   *   amountNormalized = false
   *   input: 0.25
   *   output: 0.25%
   *   input: 25
   *   output: 25%
   */
  amountNormalized = false,
}) {
  return typeof amount === 'boolean' ||
    typeof amount === 'undefined' ||
    typeof amount === 'object' ||
    Number(amount).toString() === 'NaN' ||
    Math.abs(amount) === Infinity ||
    amount === ''
    ? ''
    : Number(amountNormalized ? amount : amount / 100).toLocaleString(undefined, {
        minimumFractionDigits,
        maximumFractionDigits,
        style: showPercentSymbol ? 'percent' : 'decimal',
      });
}

export function vehicleInfo(year, make, model) {
  return `${year || ''}${year ? ' ' : ''}${make || ''}${make ? ' ' : ''}${model || ''}`;
}

export function displayDateTime(timeStamp) {
  return timeStamp && datetime(timeStamp);
}

/*
    Rich-text values authored with Quill store literal &nbsp; entities, which
    prevent normal word wrapping when re-rendered. Mirrors the same helper in
    customer-app (utility/format.js).
*/
export function replaceNonBreakingSpaces(html) {
  if (!html) return '';
  return html.replace(/&nbsp;/g, ' ');
}
